<script setup lang="ts">
import { computed, ref } from 'vue';
import store from '@/store';
import { setProfilePicture } from '@/api/user';
import CButton from '@/components/shared/CButton';
import CFileInput from '@/components/shared/CFileInput';
import CUserAvatar from '@/components/shared/CUserAvatar.vue';
import { useToaster } from '@/composables/toaster';
import { Roles } from '@/typings/user';
import { getImageDimensions, limitImageResolutionFromFile, uploadImageToS3 } from '@/utils/imageUtils';

const MAX_PICTURE_FILE_SIZE_MB = 5;
const MIN_PICTURE_SIZE = 200;
const MAX_PICTURE_SIZE = 512;

const props = defineProps<{
    collapsed?: boolean;
    hasAccountsToImpersonate?: boolean;
}>();

const emit = defineEmits<{
    (e: 'impersonate'): void;
    (e: 'impersonate-child'): void;
}>();

const toaster = useToaster();

const getInitials = (name?: string) => name?.toUpperCase().slice(0, 2);

/**
 * When the session is impersonated the card shows the user that started the impersonation,
 *  and the small badge shows the impersonated one.
 */
const userInfo = computed(() => {
    const user = store.state.user;
    const name = user?.Name || user?.Username;
    const impersonatedFrom = user?.UserImpersonatedFrom;
    const impersonatedFromName = impersonatedFrom?.Name || impersonatedFrom?.Username;
    return {
        isImpersonated: impersonatedFrom != undefined,
        fullName: impersonatedFromName || name,
        username: user?.Username,
        picturePath: impersonatedFrom ? impersonatedFrom.ProfilePicturePath : user?.ProfilePicturePath,
        impersonatedName: name,
        impersonatedInitials: getInitials(name),
        impersonatedPicturePath: user?.ProfilePicturePath
    };
});

const isImpersonateEnabled = computed(() => {
    const user = store.state.user;
    return !user?.ImpersonatedFromChildUser && (userRoles.value.has(Roles.ADMIN) || user?.ImpersonatedFromAdminUser);
});

const isSwitchTeamEnabled = computed(() => {
    const user = store.state.user;
    return (
        props.hasAccountsToImpersonate &&
        (user?.ImpersonatedFromParentUser ||
            userRoles.value.has(Roles.PARENT) ||
            user?.ImpersonatedFromChildUser ||
            userRoles.value.has(Roles.IMPERSONATE_PARENT))
    );
});

const userRoles = computed(() => {
    const user = store.state.user;
    const roles = user?.AccessRoles?.Serialized.split(',') || [];
    return new Set(roles);
});

/**
 * The endpoint updates the logged user, which is not the one displayed in the card when an
 *  admin impersonates somebody else: uploading there would silently replace the picture of
 *  the impersonated account.
 */
const canUploadPicture = computed(() => !store.state.user?.ImpersonatedFromAdminUser);

const getFileDimensions = async (file: File) => {
    const fileUrl = URL.createObjectURL(file);
    try {
        return await getImageDimensions(fileUrl);
    } finally {
        URL.revokeObjectURL(fileUrl);
    }
};

const updateUserPicture = (path: string) => {
    const user = store.state.user;
    if (!user) return;

    if (user.UserImpersonatedFrom) {
        store.commit('user', {
            ...user,
            UserImpersonatedFrom: { ...user.UserImpersonatedFrom, ProfilePicturePath: path }
        });
        return;
    }

    store.commit('user', { ...user, ProfilePicturePath: path });
};

const isUploading = ref(false);
const onPictureSelected = async (files: File[]) => {
    const [file] = files;
    if (!file || isUploading.value) return;

    if (file.size > MAX_PICTURE_FILE_SIZE_MB * 1024 * 1024) {
        toaster.error(`The profile picture must be smaller than ${MAX_PICTURE_FILE_SIZE_MB}MB.`);
        return;
    }

    isUploading.value = true;
    try {
        const { width, height } = await getFileDimensions(file);
        if (width < MIN_PICTURE_SIZE || height < MIN_PICTURE_SIZE) {
            toaster.error(`The profile picture must be at least ${MIN_PICTURE_SIZE}x${MIN_PICTURE_SIZE} pixels.`);
            return;
        }

        const { file: resizedFile } = await limitImageResolutionFromFile(file, MAX_PICTURE_SIZE, MAX_PICTURE_SIZE);

        // The resize always encodes the canvas as PNG keeping the original name, so the file is
        //  rebuilt to keep the extension, the mime type and the actual content consistent
        const pictureFile = new File([resizedFile], 'profile-picture.png', { type: 'image/png' });

        const { Path } = await uploadImageToS3('user-profile-thumbs', pictureFile);
        await setProfilePicture(Path);
        updateUserPicture(Path);
    } catch (error) {
        console.error('Failed to update the profile picture', error);
        toaster.error('The profile picture could not be updated. Please try again.');
    } finally {
        isUploading.value = false;
    }
};
</script>

<template>
    <div class="user-card" :class="{ 'bg-purple-lightest border-color-purple-light': userInfo.isImpersonated }">
        <c-file-input
            class="picture-input"
            :value="[]"
            accept=".png,.jpg,.jpeg"
            :display-preview="false"
            :disabled="!canUploadPicture || isUploading"
            @input="onPictureSelected"
        >
            <template #uploadTrigger="{ onFileUpload, disabled }">
                <c-user-avatar
                    class="avatar"
                    :class="{ uploadable: !disabled }"
                    :name="userInfo.fullName"
                    :picture-path="userInfo.picturePath"
                    :size="42"
                    @click.native="disabled ? undefined : onFileUpload()"
                >
                    <c-user-avatar
                        v-if="userInfo.isImpersonated"
                        class="impersonate-avatar"
                        circle
                        :name="userInfo.impersonatedName"
                        :picture-path="userInfo.impersonatedPicturePath"
                        :size="20"
                    />
                    <div v-if="isUploading" class="picture-overlay">
                        <font-awesome-icon class="fa-spin" :icon="['fas', 'spinner-third']" />
                    </div>
                    <div v-else-if="!disabled" class="picture-overlay hover-only">
                        <font-awesome-icon :icon="['fas', 'arrow-up-from-bracket']" />
                    </div>
                </c-user-avatar>
            </template>
        </c-file-input>
        <template v-if="!collapsed">
            <div class="user-info mr-2">
                <span class="font-weight-bold">{{ userInfo.fullName }}</span>
                <span class="text-truncate">
                    {{ userInfo.username }}
                </span>
            </div>
            <c-button
                v-if="isImpersonateEnabled"
                v-tippy="{ content: 'Impersonate', boundary: 'window' }"
                class="ml-auto color-purple"
                size="icon"
                :icon="['fas', 'face-disguise']"
                :disabled="!hasAccountsToImpersonate"
                @click="emit('impersonate')"
            />
            <c-button
                v-else-if="isSwitchTeamEnabled"
                v-tippy="{ content: 'Switch Team', boundary: 'window' }"
                size="icon"
                :icon="['fas', 'arrow-left-arrow-right']"
                @click="emit('impersonate-child')"
            />
        </template>
    </div>
</template>

<style scoped lang="scss">
@import 'styles/utilities/functions';

.user-card {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem;
    border: 1px solid get-color('gray-light');
    border-radius: 0.5rem;
    .user-info {
        display: flex;
        flex-direction: column;
        flex: 1;
        font-size: 0.875rem;
        overflow: hidden;
    }
    .picture-input {
        display: flex;
    }
    .avatar {
        border: 1px solid get-color('white');
        &.uploadable {
            cursor: pointer;
            &:hover .hover-only {
                display: flex;
            }
        }
        .impersonate-avatar {
            position: absolute;
            top: 23px;
            left: 25px;
            font-size: 0.625rem;
            background-color: get-color('warning');
        }
        .picture-overlay {
            position: absolute;
            top: 0;
            left: 0;
            display: flex;
            justify-content: center;
            align-items: center;
            width: 100%;
            height: 100%;
            font-size: 0.875rem;
            color: get-color('white');
            background-color: rgba(0, 0, 0, 0.45);
            border-radius: inherit;
            &.hover-only {
                display: none;
            }
        }
    }
}
</style>
